import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAYS = [
  { code: 'FR', flag: '🇫🇷', label: 'France' },
  { code: 'BE', flag: '🇧🇪', label: 'Belgique' },
  { code: 'CH', flag: '🇨🇭', label: 'Suisse' },
  { code: 'CA', flag: '🇨🇦', label: 'Canada' },
  { code: 'DE', flag: '🇩🇪', label: 'Allemagne' },
  { code: 'ES', flag: '🇪🇸', label: 'Espagne' },
  { code: 'IT', flag: '🇮🇹', label: 'Italie' },
  { code: 'PT', flag: '🇵🇹', label: 'Portugais' },
  { code: 'GB', flag: '🇬🇧', label: 'Royaume-Uni' },
  { code: 'US', flag: '🇺🇸', label: 'États-Unis' },
  { code: 'SN', flag: '🇸🇳', label: 'Sénégal' },
  { code: 'CI', flag: '🇨🇮', label: "Côte d'Ivoire" },
  { code: 'MA', flag: '🇲🇦', label: 'Maroc' },
  { code: 'TN', flag: '🇹🇳', label: 'Tunisie' },
  { code: 'DZ', flag: '🇩🇿', label: 'Algérie' },
  { code: 'CM', flag: '🇨🇲', label: 'Cameroun' },
  { code: 'TG', flag: '🇹🇬', label: 'Togo' },
  { code: 'BJ', flag: '🇧🇯', label: 'Bénin' },
  { code: 'ML', flag: '🇲🇱', label: 'Mali' },
  { code: 'BR', flag: '🇧🇷', label: 'Brésil' },
];

const LANGUES = [
  { code: 'en', flag: '🇬🇧', label: 'Anglais' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'es', flag: '🇪🇸', label: 'Espagnol' },
  { code: 'de', flag: '🇩🇪', label: 'Allemand' },
  { code: 'it', flag: '🇮🇹', label: 'Italien' },
  { code: 'pt', flag: '🇵🇹', label: 'Portugais' },
];

const LANGUE_UI = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'Anglais' },
  { code: 'es', label: 'Espagnol' },
  { code: 'de', label: 'Allemand' },
  { code: 'pt', label: 'Portugais' },
];

const SECTIONS_NAV = [
  { id: 'profil',     icon: '👤', label: 'Profil' },
  { id: 'password',   icon: '🔑', label: 'Mot de passe' },
  { id: '2fa',        icon: '🛡️', label: 'Authentification 2FA' },
  { id: 'apparence',  icon: '🎨', label: 'Apparence' },
  { id: 'categories', icon: '🏷️', label: 'Catégories charges' },
];

const COULEURS_PRESET = [
  '#ef4444','#f97316','#eab308','#22c55e','#14b8a6',
  '#3b82f6','#8b5cf6','#ec4899','#64748b','#0ea5e9',
];

// ── Styles ─────────────────────────────────────────────────────────────────────

const c = {
  wrap: { display: 'flex', minHeight: '100%', background: 'var(--ako-bg)', fontFamily: "'Geist', 'Inter', -apple-system, sans-serif" },
  sidebar: { width: '220px', flexShrink: 0, background: 'var(--ako-bg)', borderRight: '1px solid var(--ako-border)', padding: '2rem 0', display: 'flex', flexDirection: 'column' },
  sidebarTitle: { fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.1em', color: 'var(--ako-text-muted)', textTransform: 'uppercase', padding: '0 1.25rem', marginBottom: '0.5rem' },
  navItem: (active) => ({
    display: 'flex', alignItems: 'center', gap: '0.625rem',
    padding: '0.5rem 1.25rem', margin: '0.1rem 0.5rem', borderRadius: '0.375rem',
    cursor: 'pointer', background: active ? 'rgba(59,130,246,0.12)' : 'transparent',
    color: active ? '#58a6ff' : 'var(--ako-text-2)', fontSize: '0.875rem',
    fontWeight: active ? '600' : '400', border: 'none',
    width: 'calc(100% - 1rem)', textAlign: 'left', transition: 'all 0.15s ease',
  }),
  main: { flex: 1, padding: '2.5rem 3rem', overflowY: 'auto', maxWidth: '700px' },
  pageTitle: { fontSize: '1.5rem', fontWeight: '700', color: 'var(--ako-heading)', marginBottom: '0.25rem', marginTop: 0 },
  pageSubtitle: { color: 'var(--ako-text-muted)', fontSize: '0.875rem', marginBottom: '2.5rem' },
  section: { background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.75rem', marginBottom: '1.5rem', overflow: 'hidden' },
  sectionHeader: { padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--ako-border)' },
  sectionTitle: { fontSize: '0.9rem', fontWeight: '600', color: 'var(--ako-text)', margin: 0 },
  sectionBody: { padding: '1.5rem' },
  fieldWrap: { marginBottom: '1.25rem' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: '600', color: 'var(--ako-text-2)', marginBottom: '0.4rem', letterSpacing: '0.02em' },
  input: { width: '100%', background: 'var(--ako-input-bg)', border: '1px solid var(--ako-border-in)', borderRadius: '0.5rem', padding: '0.6rem 0.875rem', color: 'var(--ako-text)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' },
  select: { width: '100%', background: 'var(--ako-input-bg)', border: '1px solid var(--ako-border-in)', borderRadius: '0.5rem', padding: '0.6rem 0.875rem', color: 'var(--ako-text)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', cursor: 'pointer', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238b949e' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.875rem center', paddingRight: '2.5rem' },
  radioGroup: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  radioLabel: (checked) => ({ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid ' + (checked ? '#388bfd' : 'var(--ako-border-in)'), background: checked ? 'rgba(56,139,253,0.1)' : 'var(--ako-input-bg)', color: checked ? '#58a6ff' : 'var(--ako-text-2)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: checked ? '600' : '400', transition: 'all 0.15s ease', userSelect: 'none' }),
  radioCircle: (checked) => ({ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid ' + (checked ? '#388bfd' : 'var(--ako-border-in)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s ease' }),
  radioInner: { width: '6px', height: '6px', borderRadius: '50%', background: '#388bfd' },
  note: { background: 'rgba(56,139,253,0.08)', border: '1px solid rgba(56,139,253,0.2)', borderRadius: '0.5rem', padding: '0.75rem 1rem', color: '#58a6ff', fontSize: '0.8rem', lineHeight: '1.5', marginTop: '1rem' },
  langGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.625rem', marginBottom: '1rem' },
  langToggle: (active) => ({ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.625rem 0.875rem', borderRadius: '0.5rem', border: '1px solid ' + (active ? '#388bfd' : 'var(--ako-border)'), background: active ? 'rgba(56,139,253,0.1)' : 'var(--ako-input-bg)', color: active ? '#58a6ff' : 'var(--ako-text-muted)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: active ? '600' : '400', transition: 'all 0.15s ease', textAlign: 'left', userSelect: 'none' }),
  togglePip: (active) => ({ width: '8px', height: '8px', borderRadius: '50%', background: active ? '#388bfd' : 'var(--ako-border)', flexShrink: 0, marginLeft: 'auto', transition: 'background 0.15s' }),
  btnPrimary: { background: 'linear-gradient(135deg, #238636, #2ea043)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.625rem 1.25rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', transition: 'opacity 0.15s', minHeight: '44px' },
  btnDanger: { background: 'rgba(218,54,51,0.1)', color: '#f85149', border: '1px solid rgba(218,54,51,0.3)', borderRadius: '0.5rem', padding: '0.625rem 1.25rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', transition: 'all 0.15s', minHeight: '44px' },
  inputWrap: { position: 'relative' },
  eyeBtn: { position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--ako-text-muted)', cursor: 'pointer', fontSize: '1rem', padding: '0', lineHeight: 1 },
  twoFaStatus: (enabled) => ({ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0.75rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '700', background: enabled ? 'rgba(46,160,67,0.15)' : 'rgba(139,148,158,0.12)', color: enabled ? '#3fb950' : 'var(--ako-text-2)', border: '1px solid ' + (enabled ? 'rgba(46,160,67,0.3)' : 'var(--ako-border-in)') }),
  themeGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' },
  themeCard: (selected) => ({ border: '2px solid ' + (selected ? '#388bfd' : 'var(--ako-border)'), borderRadius: '0.625rem', padding: '0.875rem', cursor: 'pointer', background: selected ? 'rgba(56,139,253,0.08)' : 'var(--ako-input-bg)', transition: 'all 0.15s ease' }),
  deleteZone: { background: 'rgba(218,54,51,0.04)', border: '1px solid rgba(218,54,51,0.2)', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' },
  alert: (type) => ({ borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.85rem', marginBottom: '1rem', background: type === 'error' ? 'rgba(218,54,51,0.1)' : 'rgba(46,160,67,0.1)', border: '1px solid ' + (type === 'error' ? 'rgba(218,54,51,0.3)' : 'rgba(46,160,67,0.3)'), color: type === 'error' ? '#f85149' : '#3fb950' }),
};

// ── Shared field components ────────────────────────────────────────────────────

const FocusInput = ({ style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return <input {...props} style={{ ...style, borderColor: focused ? '#388bfd' : '#30363d', boxShadow: focused ? '0 0 0 3px rgba(56,139,253,0.12)' : 'none' }} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />;
};

const FocusSelect = ({ style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return <select {...props} style={{ ...style, borderColor: focused ? '#388bfd' : '#30363d', boxShadow: focused ? '0 0 0 3px rgba(56,139,253,0.12)' : 'none' }} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />;
};

const PasswordInput = ({ label, placeholder, value, onChange }) => {
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <div style={c.fieldWrap}>
      <label style={c.label}>{label}</label>
      <div style={c.inputWrap}>
        <input type={visible ? 'text' : 'password'} placeholder={placeholder} value={value} onChange={onChange}
          style={{ ...c.input, paddingRight: '2.5rem', borderColor: focused ? '#388bfd' : '#30363d', boxShadow: focused ? '0 0 0 3px rgba(56,139,253,0.12)' : 'none' }}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        <button type="button" style={c.eyeBtn} onClick={() => setVisible(v => !v)}>{visible ? '🙈' : '👁️'}</button>
      </div>
    </div>
  );
};

const Alert = ({ type, msg }) => msg ? <div style={c.alert(type)}>{type === 'error' ? '⚠️' : '✓'} {msg}</div> : null;

// ── Section: Profil ────────────────────────────────────────────────────────────

const SectionProfil = ({ user }) => {
  const [form, setForm]     = useState({ nom: '', email: '', langue: 'fr', genre: '', dateNaissance: '', pays: 'FR' });
  const [langues, setLangues] = useState(['fr']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState(null); // { type: 'success'|'error', msg }

  useEffect(() => {
    Promise.all([api.getProfile(), api.getPreferences()])
      .then(([profileRes, prefRes]) => {
        const u = profileRes.user || {};
        const p = prefRes.preferences || {};
        setForm({
          nom:           u.name || '',
          email:         u.email || '',
          langue:        p.langue || 'fr',
          genre:         p.genre || '',
          dateNaissance: p.dateNaissance || '',
          pays:          p.pays || 'FR',
        });
        setLangues(p.langues || ['fr']);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleLang = (code) => setLangues(l => l.includes(code) ? l.filter(x => x !== code) : [...l, code]);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await Promise.all([
        api.updateProfile({ name: form.nom, email: form.email }),
        api.updatePreferences({ langue: form.langue, genre: form.genre, dateNaissance: form.dateNaissance, pays: form.pays, langues }),
      ]);
      setStatus({ type: 'success', msg: 'Profil enregistré avec succès.' });
    } catch (e) {
      setStatus({ type: 'error', msg: e.message || 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: '#484f58', padding: '2rem', textAlign: 'center' }}>Chargement…</div>;

  return (
    <>
      {/* Profil de base */}
      <div style={c.section}>
        <div style={c.sectionHeader}><p style={c.sectionTitle}>Profil de base</p></div>
        <div style={c.sectionBody}>
          <div style={c.fieldWrap}>
            <label style={c.label}>Nom</label>
            <FocusInput type="text" placeholder="Votre nom complet" value={form.nom} onChange={set('nom')} style={c.input} />
          </div>
          <div style={c.fieldWrap}>
            <label style={c.label}>E-mail</label>
            <FocusInput type="email" placeholder="votre@email.com" value={form.email} onChange={set('email')} style={c.input} />
          </div>
          <div style={{ ...c.fieldWrap, marginBottom: 0 }}>
            <label style={c.label}>Langue de l'interface</label>
            <FocusSelect value={form.langue} onChange={set('langue')} style={c.select}>
              {LANGUE_UI.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </FocusSelect>
          </div>
        </div>
      </div>

      {/* Profil utilisateur */}
      <div style={c.section}>
        <div style={c.sectionHeader}><p style={c.sectionTitle}>Profil utilisateur</p></div>
        <div style={c.sectionBody}>
          <div style={c.fieldWrap}>
            <label style={c.label}>Genre</label>
            <div style={c.radioGroup}>
              {['Femme', 'Homme', 'Autre'].map(g => (
                <label key={g} style={c.radioLabel(form.genre === g)} onClick={() => setForm(f => ({ ...f, genre: g }))}>
                  <div style={c.radioCircle(form.genre === g)}>{form.genre === g && <div style={c.radioInner} />}</div>
                  {g}
                </label>
              ))}
            </div>
          </div>
          <div style={c.fieldWrap}>
            <label style={c.label}>Date de naissance</label>
            <FocusInput type="date" value={form.dateNaissance} onChange={set('dateNaissance')} style={{ ...c.input, colorScheme: 'dark' }} />
          </div>
          <div style={{ ...c.fieldWrap, marginBottom: 0 }}>
            <label style={c.label}>Pays</label>
            <FocusSelect value={form.pays} onChange={set('pays')} style={c.select}>
              {PAYS.map(p => <option key={p.code} value={p.code}>{p.flag} {p.label}</option>)}
            </FocusSelect>
            <div style={c.note}>
              <span style={{ marginRight: '0.5rem' }}>ℹ️</span>
              Cela vous aide à être associé aux contenus ciblant votre région.
            </div>
          </div>
        </div>
      </div>

      {/* Langues maîtrisées */}
      <div style={c.section}>
        <div style={c.sectionHeader}><p style={c.sectionTitle}>Langues maîtrisées</p></div>
        <div style={c.sectionBody}>
          <div style={c.langGrid}>
            {LANGUES.map(l => (
              <button key={l.code} type="button" style={c.langToggle(langues.includes(l.code))} onClick={() => toggleLang(l.code)}>
                <span style={{ fontSize: '1.2rem' }}>{l.flag}</span>
                <span>{l.label}</span>
                <span style={c.togglePip(langues.includes(l.code))} />
              </button>
            ))}
          </div>
          <div style={c.note}>
            <span style={{ marginRight: '0.5rem' }}>ℹ️</span>
            Sélectionnez les langues que vous pouvez utiliser.<br />
            Vous serez associé aux campagnes en fonction de ces langues.
          </div>
        </div>
      </div>

      {/* Actions */}
      <Alert type={status?.type} msg={status?.msg} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2rem' }}>
        <button type="button" style={{ ...c.btnPrimary, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Enregistrement…' : '💾 Enregistrer'}
        </button>
      </div>

      {/* Suppression compte */}
      <div style={c.deleteZone}>
        <div>
          <div style={{ color: '#f85149', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Supprimer le compte</div>
          <div style={{ color: '#484f58', fontSize: '0.8rem' }}>Cette action est irréversible. Toutes vos données seront définitivement supprimées.</div>
        </div>
        <button type="button" style={c.btnDanger}
          onClick={() => window.confirm('Êtes-vous sûr ? Cette action est irréversible.') && console.log('delete account')}>
          🗑️ Supprimer
        </button>
      </div>
    </>
  );
};

// ── Section: Mot de passe ──────────────────────────────────────────────────────

const SectionPassword = () => {
  const [form, setForm]   = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSave = async () => {
    setStatus(null);
    if (!form.current || !form.next || !form.confirm) { setStatus({ type: 'error', msg: 'Tous les champs sont requis.' }); return; }
    if (form.next !== form.confirm) { setStatus({ type: 'error', msg: 'Les nouveaux mots de passe ne correspondent pas.' }); return; }
    if (form.next.length < 8) { setStatus({ type: 'error', msg: 'Le mot de passe doit contenir au moins 8 caractères.' }); return; }
    setSaving(true);
    try {
      await api.changePassword(form.current, form.next);
      setStatus({ type: 'success', msg: 'Mot de passe mis à jour avec succès.' });
      setForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      setStatus({ type: 'error', msg: e.message || 'Erreur lors de la mise à jour.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={c.section}>
      <div style={c.sectionHeader}><p style={c.sectionTitle}>Changer le mot de passe</p></div>
      <div style={c.sectionBody}>
        <PasswordInput label="Mot de passe actuel" placeholder="••••••••" value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} />
        <PasswordInput label="Nouveau mot de passe" placeholder="Au moins 8 caractères" value={form.next} onChange={e => setForm(f => ({ ...f, next: e.target.value }))} />
        <PasswordInput label="Confirmer le nouveau mot de passe" placeholder="••••••••" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
        <Alert type={status?.type} msg={status?.msg} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" style={{ ...c.btnPrimary, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Mise à jour…' : '🔑 Mettre à jour'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Section: 2FA ───────────────────────────────────────────────────────────────

const Section2FA = () => {
  // 'idle' | 'setup' | 'enabled'
  const [step, setStep]       = useState('idle');
  const [enabled, setEnabled] = useState(false);
  const [qrCode, setQrCode]   = useState('');
  const [secret, setSecret]   = useState('');
  const [code, setCode]       = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState(null);

  useEffect(() => {
    api.getProfile().then(r => {
      const u = r.user || {};
      setEnabled(!!u.twofa_enabled);
      setStep(u.twofa_enabled ? 'enabled' : 'idle');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSetup = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const data = await api.get2faSetup();
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep('setup');
    } catch (e) {
      setStatus({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleEnable = async () => {
    if (!code || code.length !== 6) { setStatus({ type: 'error', msg: 'Entrez le code à 6 chiffres.' }); return; }
    setSaving(true);
    setStatus(null);
    try {
      await api.enable2fa(code);
      setEnabled(true);
      setStep('enabled');
      setCode('');
      setStatus({ type: 'success', msg: '2FA activée avec succès.' });
    } catch (e) {
      setStatus({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length !== 6) { setStatus({ type: 'error', msg: 'Entrez le code à 6 chiffres.' }); return; }
    setSaving(true);
    setStatus(null);
    try {
      await api.disable2fa(disableCode);
      setEnabled(false);
      setStep('idle');
      setDisableCode('');
      setStatus({ type: 'success', msg: '2FA désactivée.' });
    } catch (e) {
      setStatus({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: '#484f58', padding: '2rem', textAlign: 'center' }}>Chargement…</div>;

  return (
    <div style={c.section}>
      <div style={c.sectionHeader}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <p style={c.sectionTitle}>Authentification à deux facteurs</p>
          <span style={c.twoFaStatus(enabled)}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: enabled ? '#3fb950' : '#484f58' }} />
            {enabled ? 'Activée' : 'Désactivée'}
          </span>
        </div>
      </div>
      <div style={c.sectionBody}>
        <p style={{ color: '#8b949e', fontSize: '0.875rem', marginTop: 0, lineHeight: '1.6' }}>
          Ajoutez une couche de sécurité supplémentaire à votre compte. Une fois activée, vous devrez entrer un code temporaire à chaque connexion.
        </p>

        {status && <Alert type={status.type} msg={status.msg} />}

        {step === 'idle' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#0d1117', border: '1px solid #21262d', borderRadius: '0.5rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '1.5rem' }}>📱</span>
              <div>
                <div style={{ color: '#e6edf3', fontSize: '0.875rem', fontWeight: '600' }}>Application d'authentification</div>
                <div style={{ color: '#484f58', fontSize: '0.8rem' }}>Google Authenticator, Authy, 1Password…</div>
              </div>
            </div>
            <button type="button" style={{ ...c.btnPrimary, opacity: saving ? 0.7 : 1 }} disabled={saving} onClick={handleSetup}>
              {saving ? '⏳ Chargement…' : '🛡️ Configurer la 2FA'}
            </button>
          </div>
        )}

        {step === 'setup' && (
          <div>
            <div style={{ background: 'rgba(56,139,253,0.06)', border: '1px solid rgba(56,139,253,0.2)', borderRadius: '0.625rem', padding: '1.25rem', marginBottom: '1.25rem' }}>
              <div style={{ fontWeight: '600', color: '#e6edf3', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                1. Scannez ce QR code avec votre application d'authentification
              </div>
              {qrCode && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                  <img src={qrCode} alt="QR Code 2FA" style={{ width: '180px', height: '180px', borderRadius: '0.5rem', background: 'white', padding: '8px' }} />
                </div>
              )}
              <div style={{ fontWeight: '600', color: '#e6edf3', fontSize: '0.875rem', marginBottom: '0.4rem' }}>
                Ou entrez ce code manuellement :
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#58a6ff', background: '#0d1117', border: '1px solid #30363d', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', letterSpacing: '0.15em', wordBreak: 'break-all' }}>
                {secret}
              </div>
            </div>
            <div style={c.fieldWrap}>
              <label style={c.label}>2. Entrez le code généré par votre application</label>
              <FocusInput
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Code à 6 chiffres"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ ...c.input, letterSpacing: '0.3rem', textAlign: 'center', fontSize: '1.1rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="button" style={{ ...c.btnDanger }} onClick={() => { setStep('idle'); setCode(''); setStatus(null); }}>
                Annuler
              </button>
              <button type="button" style={{ ...c.btnPrimary, opacity: saving ? 0.7 : 1 }} disabled={saving} onClick={handleEnable}>
                {saving ? '⏳ Activation…' : '✓ Confirmer l\'activation'}
              </button>
            </div>
          </div>
        )}

        {step === 'enabled' && (
          <div>
            <div style={{ background: 'rgba(46,160,67,0.08)', border: '1px solid rgba(46,160,67,0.25)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.25rem', color: '#3fb950', fontSize: '0.875rem' }}>
              ✓ L'authentification à deux facteurs est activée sur votre compte.
            </div>
            <div style={c.fieldWrap}>
              <label style={c.label}>Entrez votre code 2FA actuel pour désactiver</label>
              <FocusInput
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Code à 6 chiffres"
                value={disableCode}
                onChange={e => setDisableCode(e.target.value.replace(/\D/g, ''))}
                style={{ ...c.input, letterSpacing: '0.3rem', textAlign: 'center', fontSize: '1.1rem' }}
              />
            </div>
            <button type="button" style={{ ...c.btnDanger, opacity: saving ? 0.7 : 1 }} disabled={saving} onClick={handleDisable}>
              {saving ? '⏳ Désactivation…' : 'Désactiver la 2FA'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Section: Apparence ────────────────────────────────────────────────────────

const THEMES = [
  { id: 'dark',  label: 'Sombre', preview: ['#0d1117', '#161b22', '#388bfd'] },
  { id: 'light', label: 'Clair',  preview: ['#f0f4f8', '#ffffff', '#388bfd'] },
  { id: 'auto',  label: 'Auto',   preview: ['#0d1117', '#f0f4f8', '#388bfd'] },
];
const DENSITIES = ['Compact', 'Normale', 'Aérée'];

const SectionApparence = () => {
  const { theme: ctxTheme, setTheme: ctxSetTheme, density: ctxDensity, setDensity: ctxSetDensity } = useApp();
  const [theme,   setTheme]   = useState(ctxTheme || 'dark');
  const [density, setDensity] = useState(ctxDensity || 'Normale');
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [status,  setStatus]  = useState(null);

  useEffect(() => {
    api.getPreferences()
      .then(r => {
        if (r.preferences?.theme)   setTheme(r.preferences.theme);
        if (r.preferences?.density) setDensity(r.preferences.density);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await api.updatePreferences({ theme, density });
      // Appliquer immédiatement via le contexte global
      ctxSetTheme(theme);
      ctxSetDensity(density);
      setStatus({ type: 'success', msg: 'Apparence enregistrée.' });
    } catch (e) {
      setStatus({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div style={c.section}>
      <div style={c.sectionHeader}><p style={c.sectionTitle}>Apparence</p></div>
      <div style={c.sectionBody}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={c.label}>Thème</label>
          <div className="grid-3col" style={c.themeGrid}>
            {THEMES.map(t => (
              <button key={t.id} type="button" style={c.themeCard(theme === t.id)} onClick={() => setTheme(t.id)}>
                <div style={{ display: 'flex', gap: '3px', marginBottom: '0.625rem' }}>
                  {t.preview.map((col, i) => <div key={i} style={{ height: '28px', flex: 1, borderRadius: '3px', background: col, border: '1px solid rgba(255,255,255,0.06)' }} />)}
                </div>
                <div style={{ fontSize: '0.8rem', color: theme === t.id ? '#58a6ff' : '#8b949e', fontWeight: theme === t.id ? '600' : '400' }}>{t.label}</div>
              </button>
            ))}
          </div>
        </div>
        <div style={{ ...c.fieldWrap, marginBottom: '1.5rem' }}>
          <label style={c.label}>Densité d'affichage</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {DENSITIES.map(d => (
              <button key={d} type="button" style={{ ...c.radioLabel(density === d), flex: 1, justifyContent: 'center' }} onClick={() => setDensity(d)}>{d}</button>
            ))}
          </div>
        </div>
        <Alert type={status?.type} msg={status?.msg} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" style={{ ...c.btnPrimary, opacity: saving ? 0.7 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Enregistrement…' : '💾 Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Section: Catégories de charges ────────────────────────────────────────────

const EMPTY_CAT = { nom: '', couleur: '#3b82f6', compte_pcg: '' };

const SectionCategories = () => {
  const [cats,    setCats]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat,  setNewCat]  = useState(EMPTY_CAT);
  const [adding,  setAdding]  = useState(false);
  const [editId,  setEditId]  = useState(null);
  const [editVal, setEditVal] = useState(EMPTY_CAT);
  const [status,  setStatus]  = useState(null);

  const load = () => {
    setLoading(true);
    api.getCategories()
      .then(r => setCats(Array.isArray(r) ? r : (r.categories || [])))
      .catch(() => setCats([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const flash = (type, msg) => {
    setStatus({ type, msg });
    setTimeout(() => setStatus(null), 3500);
  };

  const handleAdd = async () => {
    if (!newCat.nom.trim()) return flash('error', 'Le nom est requis.');
    setAdding(true);
    try {
      await api.createCategorie(newCat);
      setNewCat(EMPTY_CAT);
      load();
      flash('success', 'Catégorie créée.');
    } catch (e) {
      flash('error', e.message || 'Erreur lors de la création.');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setEditVal({ nom: cat.nom, couleur: cat.couleur || '#3b82f6', compte_pcg: cat.compte_pcg || '' });
  };

  const handleSaveEdit = async () => {
    if (!editVal.nom.trim()) return flash('error', 'Le nom est requis.');
    try {
      await api.updateCategorie(editId, editVal);
      setEditId(null);
      load();
      flash('success', 'Catégorie mise à jour.');
    } catch (e) {
      flash('error', e.message || 'Erreur lors de la mise à jour.');
    }
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer la catégorie "${nom}" ?`)) return;
    try {
      await api.deleteCategorie(id);
      load();
      flash('success', 'Catégorie supprimée.');
    } catch (e) {
      flash('error', e.message || 'Erreur lors de la suppression.');
    }
  };

  const catRow = (cat) => {
    const isEditing = editId === cat.id;
    if (isEditing) {
      return (
        <div key={cat.id} style={{ display: 'flex', gap: '0.625rem', alignItems: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid #21262d', background: 'rgba(56,139,253,0.04)', flexWrap: 'wrap' }}>
          <input type="color" value={editVal.couleur} onChange={e => setEditVal(v => ({ ...v, couleur: e.target.value }))}
            style={{ width: '36px', height: '36px', border: 'none', background: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }} />
          <input type="text" value={editVal.nom} placeholder="Nom" onChange={e => setEditVal(v => ({ ...v, nom: e.target.value }))}
            style={{ ...c.input, flex: '1 1 140px', marginBottom: 0, minWidth: '100px' }} />
          <input type="text" value={editVal.compte_pcg} placeholder="PCG ex: 606100" onChange={e => setEditVal(v => ({ ...v, compte_pcg: e.target.value }))}
            style={{ ...c.input, flex: '0 1 120px', marginBottom: 0 }} />
          <button type="button" style={{ ...c.btnPrimary, padding: '0.5rem 0.875rem', minHeight: '36px', fontSize: '0.8rem' }} onClick={handleSaveEdit}>✓</button>
          <button type="button" style={{ ...c.btnDanger, padding: '0.5rem 0.875rem', minHeight: '36px', fontSize: '0.8rem' }} onClick={() => setEditId(null)}>✕</button>
        </div>
      );
    }
    return (
      <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: '1px solid #21262d' }}>
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: cat.couleur || '#64748b', flexShrink: 0 }} />
        <span style={{ color: '#e6edf3', fontSize: '0.875rem', flex: 1 }}>{cat.nom}</span>
        {cat.compte_pcg && (
          <span style={{ fontSize: '0.75rem', color: '#484f58', fontFamily: 'monospace', background: '#0d1117', border: '1px solid #21262d', borderRadius: '0.25rem', padding: '0.1rem 0.4rem' }}>{cat.compte_pcg}</span>
        )}
        <button type="button" onClick={() => startEdit(cat)}
          style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', fontSize: '0.85rem', padding: '0.25rem 0.5rem', borderRadius: '0.375rem' }}>
          ✏️
        </button>
        <button type="button" onClick={() => handleDelete(cat.id, cat.nom)}
          style={{ background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', fontSize: '0.85rem', padding: '0.25rem 0.5rem', borderRadius: '0.375rem' }}>
          🗑️
        </button>
      </div>
    );
  };

  return (
    <div>
      {/* Ajouter une catégorie */}
      <div style={c.section}>
        <div style={c.sectionHeader}><p style={c.sectionTitle}>Nouvelle catégorie</p></div>
        <div style={{ ...c.sectionBody, paddingBottom: '1.25rem' }}>
          <p style={{ color: '#484f58', fontSize: '0.8rem', marginTop: 0, marginBottom: '1rem', lineHeight: '1.5' }}>
            Les catégories permettent de classer vos charges. Le compte PCG est utilisé dans le FEC (ex : 606000 Achats, 615000 Entretien, 622000 Honoraires…).
          </p>
          <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <label style={c.label}>Couleur</label>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
                {COULEURS_PRESET.map(col => (
                  <button key={col} type="button" onClick={() => setNewCat(v => ({ ...v, couleur: col }))}
                    style={{ width: '22px', height: '22px', borderRadius: '50%', background: col, border: newCat.couleur === col ? '2px solid white' : '2px solid transparent', cursor: 'pointer', padding: 0, flexShrink: 0 }} />
                ))}
              </div>
              <input type="color" value={newCat.couleur} onChange={e => setNewCat(v => ({ ...v, couleur: e.target.value }))}
                style={{ width: '36px', height: '28px', border: '1px solid #30363d', borderRadius: '0.375rem', background: '#0d1117', cursor: 'pointer', padding: 0 }} />
            </div>
            <div style={{ flex: '1 1 140px', minWidth: '100px' }}>
              <label style={c.label}>Nom *</label>
              <FocusInput type="text" placeholder="ex: Fournitures" value={newCat.nom} onChange={e => setNewCat(v => ({ ...v, nom: e.target.value }))} style={c.input} />
            </div>
            <div style={{ flex: '0 1 130px' }}>
              <label style={c.label}>Compte PCG</label>
              <FocusInput type="text" placeholder="606000" value={newCat.compte_pcg} onChange={e => setNewCat(v => ({ ...v, compte_pcg: e.target.value }))} style={c.input} />
            </div>
            <button type="button" style={{ ...c.btnPrimary, opacity: adding ? 0.7 : 1, alignSelf: 'flex-end', marginBottom: '0.05rem' }} disabled={adding} onClick={handleAdd}>
              {adding ? '⏳' : '+ Ajouter'}
            </button>
          </div>
          {status && <div style={{ ...c.alert(status.type), marginTop: '1rem', marginBottom: 0 }}>{status.type === 'error' ? '⚠️' : '✓'} {status.msg}</div>}
        </div>
      </div>

      {/* Liste des catégories */}
      <div style={c.section}>
        <div style={c.sectionHeader}><p style={c.sectionTitle}>Catégories existantes ({cats.length})</p></div>
        {loading ? (
          <div style={{ color: '#484f58', padding: '2rem', textAlign: 'center' }}>Chargement…</div>
        ) : cats.length === 0 ? (
          <div style={{ color: '#484f58', padding: '2rem', textAlign: 'center', fontSize: '0.875rem' }}>
            Aucune catégorie. Créez-en une ci-dessus.
          </div>
        ) : (
          <div>
            {cats.map(catRow)}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const Parametres = ({ user }) => {
  const [activeSection, setActiveSection] = useState('profil');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const current = SECTIONS_NAV.find(s => s.id === activeSection);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderSection = useCallback(() => {
    switch (activeSection) {
      case 'profil':     return <SectionProfil user={user} />;
      case 'password':   return <SectionPassword />;
      case '2fa':        return <Section2FA />;
      case 'apparence':  return <SectionApparence />;
      case 'categories': return <SectionCategories />;
      default:           return null;
    }
  }, [activeSection, user]);

  const mobileTabBtn = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.875rem 1rem',
    background: 'none',
    border: 'none',
    borderBottom: active ? '2px solid #388bfd' : '2px solid transparent',
    color: active ? '#58a6ff' : '#8b949e',
    cursor: 'pointer',
    fontWeight: active ? '700' : '400',
    fontSize: '0.825rem',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
    minHeight: '48px',
    flexShrink: 0,
  });

  if (isMobile) {
    return (
      <div style={{ background: '#0d1117', minHeight: '100%' }}>
        {/* Tab bar horizontal scrollable */}
        <div style={{ background: '#161b22', borderBottom: '1px solid #21262d', overflowX: 'auto', display: 'flex', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {SECTIONS_NAV.map(item => (
            <button key={item.id} type="button" style={mobileTabBtn(activeSection === item.id)} onClick={() => setActiveSection(item.id)}>
              <span style={{ fontSize: '1rem' }}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
        {/* Contenu */}
        <div style={{ padding: '1.5rem 1rem', maxWidth: '100%' }}>
          <h2 style={{ ...c.pageTitle, fontSize: '1.2rem', marginBottom: '0.2rem' }}>{current?.icon} {current?.label}</h2>
          <p style={{ ...c.pageSubtitle, marginBottom: '1.5rem', fontSize: '0.8rem' }}>
            {activeSection === 'profil'     && 'Gérez vos informations personnelles.'}
            {activeSection === 'password'   && 'Modifiez votre mot de passe.'}
            {activeSection === '2fa'        && 'Sécurisez votre compte avec la 2FA.'}
            {activeSection === 'apparence'  && "Personnalisez l'interface."}
            {activeSection === 'categories' && 'Gérez vos catégories de charges.'}
          </p>
          {renderSection()}
        </div>
      </div>
    );
  }

  return (
    <div style={c.wrap}>
      <div style={c.sidebar}>
        <p style={c.sidebarTitle}>Paramètres</p>
        {SECTIONS_NAV.map(item => (
          <button key={item.id} type="button" style={c.navItem(activeSection === item.id)} onClick={() => setActiveSection(item.id)}>
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
      <div style={c.main}>
        <h2 style={c.pageTitle}>{current?.icon} {current?.label}</h2>
        <p style={c.pageSubtitle}>
          {activeSection === 'profil'     && 'Gérez vos informations personnelles et vos préférences.'}
          {activeSection === 'password'   && 'Mettez à jour votre mot de passe pour sécuriser votre compte.'}
          {activeSection === '2fa'        && 'Renforcez la sécurité de votre compte avec une double authentification.'}
          {activeSection === 'apparence'  && "Personnalisez l'apparence de l'interface."}
          {activeSection === 'categories' && 'Créez et gérez les catégories de vos charges (utilisées dans les écritures et le FEC).'}
        </p>
        {renderSection()}
      </div>
    </div>
  );
};

export default Parametres;
